import React from 'react';
import Icon from '../../../components/AppIcon';

const UsageMetrics = ({ usage, limits }) => {
  const getUsagePercentage = (used, limit) => {
    if (limit === 'unlimited') return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-error';
    if (percentage >= 75) return 'text-warning';
    return 'text-success';
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-error';
    if (percentage >= 75) return 'bg-warning';
    return 'bg-success';
  };

  const metrics = [
    {
      id: 'api_calls',
      name: 'API Calls',
      icon: 'Zap',
      used: usage?.apiCalls,
      limit: limits?.apiCalls,
      description: 'Monthly API requests'
    },
    {
      id: 'predictions',
      name: 'AI Predictions',
      icon: 'TrendingUp',
      used: usage?.predictions,
      limit: limits?.predictions,
      description: 'Daily prediction access'
    },
    {
      id: 'signals',
      name: 'Trading Signals',
      icon: 'Bell',
      used: usage?.signals,
      limit: limits?.signals,
      description: 'Daily signal alerts'
    },
    {
      id: 'portfolios',
      name: 'Portfolios',
      icon: 'PieChart',
      used: usage?.portfolios,
      limit: limits?.portfolios,
      description: 'Active portfolios'
    },
    {
      id: 'storage',
      name: 'Data Storage',
      icon: 'Database',
      used: usage?.storage,
      limit: limits?.storage,
      description: 'Historical data storage',
      unit: 'GB'
    },
    {
      id: 'support',
      name: 'Support Tickets',
      icon: 'MessageCircle',
      used: usage?.supportTickets,
      limit: limits?.supportTickets,
      description: 'Monthly support requests'
    }
  ];

  const formatLimit = (limit) => {
    if (limit === 'unlimited') return 'Unlimited';
    return limit?.toLocaleString();
  };

  const formatUsed = (used, unit = '') => {
    return `${used?.toLocaleString()}${unit}`;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-card-foreground">Usage & Limits</h2>
        <p className="text-sm text-muted-foreground">Monitor your plan usage and limits</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics?.map((metric) => {
          const percentage = getUsagePercentage(metric?.used, metric?.limit);
          const isUnlimited = metric?.limit === 'unlimited';
          
          return (
            <div key={metric?.id} className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={metric?.icon} size={16} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{metric?.name}</h3>
                    <p className="text-xs text-muted-foreground">{metric?.description}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Usage</span>
                  <span className={`text-sm font-medium ${getUsageColor(percentage)}`}>
                    {formatUsed(metric?.used, metric?.unit)} / {formatLimit(metric?.limit)}
                  </span>
                </div>

                {!isUnlimited && (
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(percentage)}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                )}

                {!isUnlimited && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0</span>
                    <span>{percentage?.toFixed(1)}%</span>
                    <span>{formatLimit(metric?.limit)}</span>
                  </div>
                )}

                {isUnlimited && (
                  <div className="flex items-center space-x-2">
                    <Icon name="Infinity" size={16} className="text-success" />
                    <span className="text-xs text-success">Unlimited usage</span>
                  </div>
                )}
              </div>
              {percentage >= 90 && !isUnlimited && (
                <div className="mt-3 p-2 bg-error/10 border border-error/20 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={14} className="text-error" />
                    <span className="text-xs text-error">Approaching limit</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div>
            <p className="text-sm font-medium text-card-foreground">Usage Reset Information</p>
            <p className="text-xs text-muted-foreground mt-1">
              Monthly limits reset on the 9th of each month. Daily limits reset at midnight UTC.
              Upgrade your plan for higher limits and unlimited features.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageMetrics;